"use client";

import type { ComponentType, PropsWithChildren } from "react";

import dynamic from "next/dynamic";

// Side-effect import: registers bellhop's client-side services via
// configureServices() at module load. Must run before any client hook or
// effect calls a bellhop endpoint from the browser. Whitelisted in
// `package.json`'s `sideEffects` so it survives tree-shaking.
import "~/bellhop.client";
import { MessagesDataProvider } from "~/context/MessagesDataContext";
import { NavbarSettingsProvider } from "~/context/NavbarSettingsProvider";
import { Locale } from "~/i18n/i18n.types";
import { IntlProvider } from "~/i18n/IntlProvider";
import { composeProviders, provider } from "~/lib/react/compose-providers";
import { QueryProvider } from "~/lib/react-query/QueryProvider";
import { AppModalsProvider } from "~/modals";
import { StatsigBootstrapProvider } from "~/statsig/StatsigBootstrapProvider";
import { StatsigUserProvider } from "~/statsig/StatsigUserProvider";

import { GoogleOneTapProvider } from "./GoogleOneTapProvider";
import { ProfileProvider } from "./ProfileProvider";

/**
 * MSW is dev-only tooling — dynamic import so its (fairly heavy) mock code
 * doesn't ship in prod. The dynamic loader is wrapped in `.catch` so that a
 * chunk fetch failure (e.g. through a reverse proxy that mishandles hashed
 * chunks) degrades to a passthrough, letting the app render without mocks
 * instead of blanking the entire Providers tree.
 */
const PassthroughMSWProvider: ComponentType<PropsWithChildren> = ({
  children,
}) => <>{children}</>;

const MSWProvider: ComponentType<PropsWithChildren> =
  process.env.NODE_ENV === "development"
    ? dynamic(
        () =>
          import("~/components/msw/MSWProvider")
            .then((m) => m.MSWProvider)
            .catch((error) => {
              // eslint-disable-next-line no-console
              console.warn(
                "[MSW] Failed to load — running without mocks:",
                error,
              );
              return PassthroughMSWProvider;
            }),
        // ssr: false so Next doesn't emit a `<link rel="preload">` for the
        // MSW chunk in the SSR HTML — the preload occasionally 404s when
        // Turbopack's guessed chunk hash doesn't match what it later
        // generates on demand. MSW runs client-side only anyway.
        { ssr: false },
      )
    : PassthroughMSWProvider;

type ProvidersProps = PropsWithChildren<{
  bootstrapValues: string;
  googleClientId: string;
  isBot: boolean;
  locale: Locale;
}>;

/**
 * The client-side provider tree. Server-computed values arrive as props from
 * `layout.tsx`, which must also keep the `"use server"` `EnvDataProvider` above this.
 * Listed outermost first; each `provider(...)` type-checks its own props.
 */
export const Providers = ({
  bootstrapValues,
  children,
  googleClientId,
  isBot,
  locale,
}: ProvidersProps) =>
  composeProviders(
    [
      provider(IntlProvider, { locale }), // Outermost (Level 1)
      provider(MSWProvider),
      provider(QueryProvider),
      provider(ProfileProvider),
      provider(GoogleOneTapProvider, { clientId: googleClientId }),
      provider(MessagesDataProvider),
      // Outermost Statsig Provider - bootstraps the SDK client (init)
      provider(StatsigBootstrapProvider, {
        disabled: isBot,
        values: bootstrapValues,
      }),
      // Innermost Statsig Provider — single writer for the Statsig user (attributes + readiness)
      provider(StatsigUserProvider, { disabled: isBot }),
      // Lets a route hand the navbar the element that turns it opaque, so it has to
      // sit above both the navbar and the page in `layout.tsx`.
      provider(NavbarSettingsProvider),
      // Innermost (last level) so the modals it renders can read every context above.
      provider(AppModalsProvider),
    ],
    children,
  );
